/**
 * `TrustDisputesView` — ADR-0235 ops2026 Agents → Trust disputes.
 *
 * Merchants press "This is wrong" on a trust label; each report lands
 * here. Staff resolve it:
 *   - Upheld     — the label stays (optional note).
 *   - Overturned — the label is hidden for that site + agent until the
 *                  rule version changes, and the case is recorded as a
 *                  merchant-feedback labelled example (weight capped per
 *                  account). A note is required.
 * Opening the queue is staff-audited (merchant notes are free text).
 */

import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { reasonCopy, type DisputableLabel } from '@sill/types';
import type { AdminTrustDisputeView, AdminTrustDisputesResponse } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export const DISPUTED_LABEL_TEXT: Readonly<Record<DisputableLabel, string>> = {
  verified: 'Verified',
  likely_impersonator: 'Likely impersonator',
  suspicious: 'Suspicious',
};

type Tab = 'open' | 'resolved';

function snapshotLine(s: Record<string, unknown>): string {
  const counts = (typeof s['counts'] === 'object' && s['counts'] !== null ? s['counts'] : {}) as Record<string, unknown>;
  const n = (k: string): number => (typeof counts[k] === 'number' ? (counts[k] as number) : 0);
  const label = typeof s['label'] === 'string' ? s['label'] : '?';
  return `label ${label} · ${n('total')} requests · ${n('verified')} verified · ${n('likely_impersonator')} impersonator · ${n('suspicious')} suspicious`;
}

export function TrustDisputesView({ onCountChange }: { readonly onCountChange?: (n: number) => void } = {}): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [tab, setTab] = useState<Tab>('open');
  const [data, setData] = useState<AdminTrustDisputesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.getTrustDisputes(tab);
      setData(r);
      setMissing(false);
      onCountChange?.(r.open_count);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'not_found') setMissing(true);
      else setError(err instanceof ApiError ? err.message : 'Failed to load disputes.');
    } finally {
      setLoading(false);
    }
  }, [api, tab, onCountChange]);

  useEffect(() => { void load(); }, [load]);

  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const };

  return (
    <div style={{ padding: 24, maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ ...kicker, marginBottom: 8 }}>ADR-0235 · Agent Trust Engine</div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Trust disputes</h1>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 4, maxWidth: 680, lineHeight: 1.5 }}>
            Merchants report labels they think are wrong. Overturning hides that label for that site and agent until the rule version changes, and records the case as a merchant label for the backtest.
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 12, padding: '6px 12px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div role="tablist" aria-label="Dispute status" style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {(['open', 'resolved'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 10px', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === t ? C.text : 'transparent'}`, color: tab === t ? C.text : C.muted, cursor: 'pointer' }}
          >
            {t === 'open' ? `Open${data !== null ? ` (${data.open_count})` : ''}` : 'Resolved'}
          </button>
        ))}
      </div>

      {missing ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>The disputes API is not deployed yet. This page will work once it is live.</div>
        </Card>
      ) : null}
      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}><div role="alert" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div></Card>
      ) : null}
      {loading && data === null ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}
      {data !== null && data.disputes.length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }} data-testid="disputes-empty">{tab === 'open' ? 'No open disputes.' : 'No resolved disputes yet.'}</div>
      ) : null}
      {data?.disputes.map((d) => <DisputeRow key={d.dispute_id} d={d} onResolved={() => void load()} />)}
    </div>
  );
}

function DisputeRow({ d, onResolved }: { readonly d: AdminTrustDisputeView; readonly onResolved: () => void }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [outcome, setOutcome] = useState<'upheld' | 'overturned' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = d.claim_key.startsWith('token:') ? d.claim_key.slice(6) : d.claim_key;
  const ctx = { name, operator: null, signer: null };

  const submit = async (): Promise<void> => {
    if (outcome === null) return;
    setBusy(true);
    setError(null);
    try {
      await api.resolveTrustDispute(d.dispute_id, outcome, note.trim());
      onResolved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not resolve the dispute.');
    } finally {
      setBusy(false);
    }
  };

  const small = { fontFamily: monoFont, fontSize: 11, color: C.muted };
  return (
    <Card style={{ padding: 16, marginBottom: 12 }} data-testid={`dispute-${d.dispute_id}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>{name}</span>
        <Pill tone={d.disputed_label === 'likely_impersonator' ? 'coral' : d.disputed_label === 'suspicious' ? 'amber' : 'lime'}>
          disputes: {DISPUTED_LABEL_TEXT[d.disputed_label]}
        </Pill>
        {d.status !== 'open' ? <Pill tone={d.status === 'overturned' ? 'sky' : 'neutral'}>{d.status}</Pill> : null}
        <span style={small}>{d.site_domain ?? d.site_id} · {d.account_name ?? d.account_id}</span>
        <span style={{ ...small, marginLeft: 'auto' }}>{formatIsoCompact(d.created_at)} · {d.rule_version}</span>
      </div>
      <div style={{ ...small, marginTop: 6 }}>{snapshotLine(d.verdict_snapshot)}</div>
      {d.reasons.length > 0 ? (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontFamily: sansFont, fontSize: 12.5, color: C.body, lineHeight: 1.5 }}>
          {d.reasons.map((r) => <li key={r}>{reasonCopy(r, ctx)} <span style={{ ...small, fontSize: 10 }}>{r}</span></li>)}
        </ul>
      ) : null}
      <div style={{ marginTop: 10, padding: 10, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 3, fontFamily: sansFont, fontSize: 13, color: d.note === null ? C.muted : C.text, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {d.note ?? 'No note from the merchant.'}
      </div>
      {d.status !== 'open' ? (
        <div style={{ ...small, marginTop: 8 }}>
          Resolved {d.resolved_at !== null ? formatIsoCompact(d.resolved_at) : ''}{d.resolution_note !== null ? ` — ${d.resolution_note}` : ''}
        </div>
      ) : (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['upheld', 'overturned'] as const).map((o) => (
              <button
                key={o}
                type="button"
                aria-pressed={outcome === o}
                data-testid={`resolve-${o}`}
                onClick={() => setOutcome(o)}
                style={{ fontFamily: sansFont, fontSize: 12, padding: '5px 10px', borderRadius: 3, cursor: 'pointer', background: outcome === o ? C.surface3 : 'transparent', color: C.text, border: `1px solid ${outcome === o ? C.text : C.border}` }}
              >
                {o === 'upheld' ? 'Uphold (label stays)' : 'Overturn (hide label)'}
              </button>
            ))}
          </div>
          {outcome !== null ? (
            <>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                rows={2}
                placeholder={outcome === 'overturned' ? 'Why the label is wrong (required)' : 'Note (optional)'}
                data-testid="resolve-note"
                style={{ fontFamily: sansFont, fontSize: 13, padding: 8, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, resize: 'vertical' }}
              />
              <div>
                <button
                  type="button"
                  disabled={busy || (outcome === 'overturned' && note.trim().length === 0)}
                  data-testid="resolve-submit"
                  onClick={() => void submit()}
                  style={{ fontFamily: sansFont, fontSize: 12, padding: '6px 12px', borderRadius: 3, cursor: 'pointer', background: C.text, color: C.bg, border: 'none', opacity: busy || (outcome === 'overturned' && note.trim().length === 0) ? 0.5 : 1 }}
                >
                  {outcome === 'upheld' ? 'Uphold' : 'Overturn'}
                </button>
              </div>
            </>
          ) : null}
          {error !== null ? <div role="alert" style={{ fontFamily: sansFont, fontSize: 12.5, color: C.coralFg }}>{error}</div> : null}
        </div>
      )}
    </Card>
  );
}
